// frontend/src/AdminSupport.js
// ═══════════════════════════════════════════════════════════════════════════
// CENTRO DE SOPORTE Y ADMINISTRACIÓN - UrbanQ
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from './config/api';
import './AdminSupport.css';
import './styles/AdminTabs.css';
import {
    FiHeadphones, FiServer, FiShield, FiDatabase, FiDownload,
    FiClock, FiCheck, FiAlertCircle, FiAlertTriangle, FiX,
    FiMail, FiGlobe, FiCpu, FiHardDrive, FiActivity,
    FiLock, FiEye, FiEyeOff, FiRefreshCw, FiSend,
    FiPaperclip, FiChevronRight, FiInfo, FiUsers, FiDollarSign,
    FiBell, FiSettings, FiExternalLink, FiMessageSquare
} from 'react-icons/fi';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export default function AdminSupport() {
    // Estados
    const [activeTab, setActiveTab] = useState('info');
    const [loading, setLoading] = useState(false);
    const [systemInfo, setSystemInfo] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [backups, setBackups] = useState([]);
    const [notification, setNotification] = useState(null);
    
    // Estados del formulario de ticket
    const [ticketForm, setTicketForm] = useState({
        subject: '',
        description: '',
        category: 'consulta',
        priority: 'media'
    });
    const [ticketAttachment, setTicketAttachment] = useState(null);
    
    // Estados del formulario de contraseña
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    
    // Estados de backup
    const [backupType, setBackupType] = useState('completo');
    const [backupFormat, setBackupFormat] = useState('xlsx');
    const [sendBackupEmail, setSendBackupEmail] = useState(true);
    const [generatingBackup, setGeneratingBackup] = useState(false);

    // Obtener usuario actual
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE API
    // ═══════════════════════════════════════════════════════════════════════

    const fetchSystemInfo = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(API_ENDPOINTS.supportInfo, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setSystemInfo(data.systemInfo);
            }
        } catch (error) {
            console.error('Error fetching system info:', error);
            showNotification('Error al cargar información del sistema', 'error');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchMyTickets = useCallback(async () => {
        try {
            const response = await fetch(`${API_ENDPOINTS.supportTickets}?limit=20`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setTickets(data.tickets);
            }
        } catch (error) {
            console.error('Error fetching tickets:', error);
        }
    }, [token]);

    const fetchBackupHistory = useCallback(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.supportBackups, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setBackups(data.backups);
            }
        } catch (error) {
            console.error('Error fetching backups:', error);
        }
    }, [token]);

    // Cargar datos iniciales
    useEffect(() => {
        fetchSystemInfo();
        fetchMyTickets();
        fetchBackupHistory();
    }, [fetchSystemInfo, fetchMyTickets, fetchBackupHistory]);

    // ═══════════════════════════════════════════════════════════════════════
    // NOTIFICACIONES
    // ═══════════════════════════════════════════════════════════════════════

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CREAR TICKET
    // ═══════════════════════════════════════════════════════════════════════

    const handleCreateTicket = async (e) => {
        e.preventDefault();

        if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
            showNotification('Por favor completa todos los campos', 'error');
            return;
        }

        try {
            setLoading(true);

            // Crear FormData para enviar archivo adjunto
            const formData = new FormData();
            formData.append('subject', ticketForm.subject);
            formData.append('description', ticketForm.description);
            formData.append('category', ticketForm.category);
            formData.append('priority', ticketForm.priority);

            // Agregar archivo si existe
            if (ticketAttachment) {
                formData.append('attachment', ticketAttachment);
            }

            const response = await fetch(API_ENDPOINTS.supportTickets, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // NO incluir Content-Type, el navegador lo establece automáticamente para FormData
                },
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                const attachmentMsg = data.ticket.hasAttachment ? ' (con archivo adjunto)' : '';
                showNotification(`Ticket ${data.ticket.ticketNumber} creado exitosamente${attachmentMsg}. Revisa tu correo.`, 'success');
                setTicketForm({ subject: '', description: '', category: 'consulta', priority: 'media' });
                setTicketAttachment(null);
                // Limpiar el input de archivo
                const fileInput = document.getElementById('ticket-attachment-input');
                if (fileInput) fileInput.value = '';
                fetchMyTickets();
                setActiveTab('tickets');
            } else {
                showNotification(data.message || 'Error al crear ticket', 'error');
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            showNotification('Error al crear el ticket', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Manejar selección de archivo
    const handleAttachmentChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tamaño (5MB máximo)
            if (file.size > 5 * 1024 * 1024) {
                showNotification('El archivo es muy grande. Máximo 5MB permitido.', 'error');
                e.target.value = '';
                return;
            }
            // Validar tipo
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf',
                'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                showNotification('Tipo de archivo no permitido. Solo JPG, PNG, GIF, PDF, DOC, DOCX.', 'error');
                e.target.value = '';
                return;
            }
            setTicketAttachment(file);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // GENERAR BACKUP
    // ═══════════════════════════════════════════════════════════════════════

    const handleGenerateBackup = async () => {
        try {
            setGeneratingBackup(true);
            const response = await fetch(API_ENDPOINTS.supportBackup, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    backupType,
                    format: backupFormat,
                    sendEmail: sendBackupEmail
                })
            });

            const data = await response.json();

            if (data.success) {
                showNotification(data.message, 'success');
                fetchBackupHistory();
            } else {
                showNotification(data.message || 'Error al generar backup', 'error');
            }
        } catch (error) {
            console.error('Error generating backup:', error);
            showNotification('Error al generar el backup', 'error');
        } finally {
            setGeneratingBackup(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CAMBIAR CONTRASEÑA
    // ═══════════════════════════════════════════════════════════════════════

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showNotification('Las contraseñas nuevas no coinciden', 'error');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            showNotification('La contraseña debe tener al menos 6 caracteres', 'error');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(API_ENDPOINTS.supportChangePassword, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(passwordForm)
            });

            const data = await response.json();
            
            if (data.success) {
                showNotification('Contraseña actualizada exitosamente', 'success');
                setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                showNotification(data.message || 'Error al cambiar contraseña', 'error');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            showNotification('Error al cambiar la contraseña', 'error');
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDERIZADO
    // ═══════════════════════════════════════════════════════════════════════

    return (
        <div className="urbanq-support-container">
            {/* Notificación */}
            {notification && (
                <div className={`urbanq-support-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FiX /></button>
                </div>
            )}

            {/* Tabs */}
            <div className="urbanq-admin-tabs">
                <button
                    className={`urbanq-admin-tab ${activeTab === 'info' ? 'active' : ''}`}
                    onClick={() => setActiveTab('info')}
                >
                    <FiInfo /> Información
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'tickets' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tickets')}
                >
                    <FiMessageSquare /> Tickets de Soporte
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'create-ticket' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create-ticket')}
                >
                    <FiSend /> Crear Ticket
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'backup' ? 'active' : ''}`}
                    onClick={() => setActiveTab('backup')}
                >
                    <FiDownload /> Backup de Datos
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveTab('security')}
                >
                    <FiShield /> Seguridad
                </button>
            </div>

            {/* Contenido */}
            <div className="urbanq-support-content">
                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: INFORMACIÓN DEL SISTEMA */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'info' && (
                    <div className="urbanq-support-info-tab">
                        {/* Información de la plataforma */}
                        <div className="urbanq-support-section">
                            <div className="urbanq-support-section-header">
                                <h2><FiInfo /> Información de la Plataforma</h2>
                                <button 
                                    className="urbanq-support-refresh-btn"
                                    onClick={fetchSystemInfo}
                                    disabled={loading}
                                >
                                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                                </button>
                            </div>
                            
                            <div className="urbanq-support-info-grid">
                                <div className="urbanq-support-info-card">
                                    <div className="urbanq-support-info-card-icon purple">
                                        <FiServer />
                                    </div>
                                    <div className="urbanq-support-info-card-content">
                                        <span className="label">Versión</span>
                                        <span className="value">{systemInfo?.version || '2.1.0'}</span>
                                    </div>
                                </div>
                                
                                <div className="urbanq-support-info-card">
                                    <div className="urbanq-support-info-card-icon blue">
                                        <FiClock />
                                    </div>
                                    <div className="urbanq-support-info-card-content">
                                        <span className="label">Última Actualización</span>
                                        <span className="value">{systemInfo?.lastUpdate || '27/01/2025'}</span>
                                    </div>
                                </div>
                                
                                <div className="urbanq-support-info-card">
                                    <div className="urbanq-support-info-card-icon green">
                                        <FiActivity />
                                    </div>
                                    <div className="urbanq-support-info-card-content">
                                        <span className="label">Uptime</span>
                                        <span className="value">{systemInfo?.services?.server?.uptimeFormatted || '99.9%'}</span>
                                    </div>
                                </div>
                                
                                <div className="urbanq-support-info-card">
                                    <div className="urbanq-support-info-card-icon orange">
                                        <FiMail />
                                    </div>
                                    <div className="urbanq-support-info-card-content">
                                        <span className="label">Email Soporte</span>
                                        <span className="value small">{systemInfo?.supportEmail || 'admin@nextunicodecorp.com'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Estado de Servicios */}
                        <div className="urbanq-support-section">
                            <h2><FiServer /> Estado de Servicios</h2>
                            <div className="urbanq-support-services-grid">
                                <ServiceStatusCard 
                                    name="Servidores"
                                    status={systemInfo?.services?.server?.status || 'operativo'}
                                    icon={<FiServer />}
                                    detail={`Uptime: ${systemInfo?.services?.server?.uptimeFormatted || 'N/A'}`}
                                />
                                <ServiceStatusCard 
                                    name="Base de Datos"
                                    status={systemInfo?.services?.database?.status || 'operativo'}
                                    icon={<FiDatabase />}
                                    detail={systemInfo?.services?.database?.type || 'MySQL'}
                                />
                                <ServiceStatusCard 
                                    name="Almacenamiento"
                                    status={systemInfo?.services?.storage?.status || 'operativo'}
                                    icon={<FiHardDrive />}
                                    detail={`Usado: ${systemInfo?.services?.storage?.used || 'N/A'}`}
                                />
                                <ServiceStatusCard 
                                    name="Servicio de Emails"
                                    status={systemInfo?.services?.email?.status || 'operativo'}
                                    icon={<FiMail />}
                                    detail={systemInfo?.services?.email?.provider || 'Gmail SMTP'}
                                />
                                <ServiceStatusCard 
                                    name="Registro de Pagos"
                                    status={systemInfo?.services?.payments?.status || 'operativo'}
                                    icon={<FiDollarSign />}
                                    detail={systemInfo?.services?.payments?.provider || 'PayPal'}
                                />
                            </div>
                        </div>

                        {/* Regiones de Servidores */}
                        <div className="urbanq-support-section">
                            <h2><FiGlobe /> Regiones de Servidores</h2>
                            <div className="urbanq-support-regions-grid">
                                <div className="urbanq-support-region-card">
                                    <div className="urbanq-support-region-header">
                                        <span className="urbanq-support-region-flag">🇺🇸</span>
                                        <span className="urbanq-support-region-name">USA (Principal)</span>
                                        <span className="urbanq-support-region-status operativo">Activo</span>
                                    </div>
                                    <div className="urbanq-support-region-details">
                                        <p><strong>Ubicación:</strong> Virginia, US-East-1</p>
                                        <p><strong>Proveedor:</strong> AWS</p>
                                        <p><strong>Latencia:</strong> ~45ms</p>
                                    </div>
                                </div>
                                
                                <div className="urbanq-support-region-card">
                                    <div className="urbanq-support-region-header">
                                        <span className="urbanq-support-region-flag">🇧🇷</span>
                                        <span className="urbanq-support-region-name">LATAM (Secundario)</span>
                                        <span className="urbanq-support-region-status operativo">Activo</span>
                                    </div>
                                    <div className="urbanq-support-region-details">
                                        <p><strong>Ubicación:</strong> São Paulo, SA-East-1</p>
                                        <p><strong>Proveedor:</strong> AWS</p>
                                        <p><strong>Latencia:</strong> ~25ms</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Métricas del Sistema */}
                        <div className="urbanq-support-section">
                            <h2><FiCpu /> Métricas del Sistema</h2>
                            <div className="urbanq-support-metrics-grid">
                                <MetricCard 
                                    label="Carga del Servidor"
                                    value={systemInfo?.metrics?.serverLoad || '23%'}
                                    icon={<FiCpu />}
                                    color="blue"
                                />
                                <MetricCard 
                                    label="Memoria Utilizada"
                                    value={systemInfo?.metrics?.memoryUsed || '45%'}
                                    icon={<FiHardDrive />}
                                    color="purple"
                                />
                                <MetricCard 
                                    label="Usuarios Activos"
                                    value={systemInfo?.condominiumStats?.totalUsers || '0'}
                                    icon={<FiUsers />}
                                    color="green"
                                />
                                <MetricCard 
                                    label="Disponibilidad"
                                    value="99.98%"
                                    icon={<FiActivity />}
                                    color="teal"
                                />
                            </div>
                        </div>

                        {/* Actividad Reciente */}
                        <div className="urbanq-support-section">
                            <h2><FiClock /> Actividad Reciente del Sistema</h2>
                            <div className="urbanq-support-activity-list">
                                {systemInfo?.recentActivity?.length > 0 ? (
                                    systemInfo.recentActivity.map((log, index) => (
                                        <div key={index} className={`urbanq-support-activity-item ${log.type}`}>
                                            <div className="urbanq-support-activity-icon">
                                                {log.type === 'info' ? <FiInfo /> : 
                                                 log.type === 'warning' ? <FiAlertTriangle /> : 
                                                 log.type === 'error' ? <FiAlertCircle /> : <FiCheck />}
                                            </div>
                                            <div className="urbanq-support-activity-content">
                                                <span className="urbanq-support-activity-action">{log.action}</span>
                                                <span className="urbanq-support-activity-desc">{log.description}</span>
                                            </div>
                                            <span className="urbanq-support-activity-time">
                                                {new Date(log.createdAt).toLocaleString('es-EC', { 
                                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                                                })}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="urbanq-support-no-data">
                                        <FiInfo />
                                        <p>No hay actividad reciente registrada</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: TICKETS DE SOPORTE */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'tickets' && (
                    <div className="urbanq-support-tickets-tab">
                        <div className="urbanq-support-section">
                            <div className="urbanq-support-section-header">
                                <h2><FiMessageSquare /> Mis Tickets de Soporte</h2>
                                <button 
                                    className="urbanq-support-btn primary"
                                    onClick={() => setActiveTab('create-ticket')}
                                >
                                    <FiSend /> Crear Nuevo Ticket
                                </button>
                            </div>

                            {tickets.length > 0 ? (
                                <div className="urbanq-support-tickets-table-container">
                                    <table className="urbanq-support-tickets-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Asunto</th>
                                                <th>Estado</th>
                                                <th>Prioridad</th>
                                                <th>Fecha</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tickets.map(ticket => (
                                                <tr key={ticket.id}>
                                                    <td className="ticket-id">{ticket.ticket_number}</td>
                                                    <td className="ticket-subject">{ticket.subject}</td>
                                                    <td>
                                                        <span className={`urbanq-support-status-badge ${ticket.status}`}>
                                                            {ticket.status === 'abierto' && <FiClock />}
                                                            {ticket.status === 'en_proceso' && <FiRefreshCw />}
                                                            {ticket.status === 'resuelto' && <FiCheck />}
                                                            {ticket.status === 'cerrado' && <FiX />}
                                                            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1).replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`urbanq-support-priority-badge ${ticket.priority}`}>
                                                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td className="ticket-date">
                                                        {new Date(ticket.created_at).toLocaleDateString('es-EC')}
                                                    </td>
                                                    <td>
                                                        <button className="urbanq-support-action-btn">
                                                            <FiEye /> Ver
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="urbanq-support-empty-state">
                                    <FiMessageSquare className="urbanq-support-empty-icon" />
                                    <h3>No tienes tickets de soporte</h3>
                                    <p>Cuando crees un ticket, aparecerá aquí para dar seguimiento</p>
                                    <button 
                                        className="urbanq-support-btn primary"
                                        onClick={() => setActiveTab('create-ticket')}
                                    >
                                        <FiSend /> Crear mi primer ticket
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: CREAR TICKET */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'create-ticket' && (
                    <div className="urbanq-support-create-ticket-tab">
                        <div className="urbanq-support-section">
                            <h2><FiSend /> Crear Nuevo Ticket de Soporte</h2>
                            <p className="urbanq-support-section-desc">
                                Describe tu problema o consulta y nuestro equipo te responderá lo antes posible.
                                Recibirás una confirmación en tu correo electrónico.
                            </p>

                            <form onSubmit={handleCreateTicket} className="urbanq-support-ticket-form">
                                <div className="urbanq-support-form-row">
                                    <div className="urbanq-support-form-group">
                                        <label>Categoría</label>
                                        <select 
                                            value={ticketForm.category}
                                            onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                                        >
                                            <option value="error_sistema">Error en el Sistema</option>
                                            <option value="facturacion">Facturación</option>
                                            <option value="funcionalidad">Nueva Funcionalidad</option>
                                            <option value="consulta">Consulta General</option>
                                            <option value="otro">Otro</option>
                                        </select>
                                    </div>

                                    <div className="urbanq-support-form-group">
                                        <label>Prioridad</label>
                                        <select 
                                            value={ticketForm.priority}
                                            onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                                        >
                                            <option value="baja">Baja</option>
                                            <option value="media">Media</option>
                                            <option value="alta">Alta</option>
                                            <option value="urgente">Urgente</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label>Asunto *</label>
                                    <input 
                                        type="text"
                                        value={ticketForm.subject}
                                        onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                                        placeholder="Describe brevemente el problema"
                                        required
                                    />
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label>Descripción *</label>
                                    <textarea
                                        value={ticketForm.description}
                                        onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                                        placeholder="Proporciona todos los detalles sobre el problema o solicitud..."
                                        rows={6}
                                        required
                                    />
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label><FiPaperclip /> Archivo Adjunto (Opcional)</label>
                                    <div className="urbanq-support-file-upload">
                                        <input
                                            type="file"
                                            id="ticket-attachment-input"
                                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                                            onChange={handleAttachmentChange}
                                        />
                                        {ticketAttachment && (
                                            <div className="urbanq-support-file-info">
                                                <span className="urbanq-support-file-name">
                                                    <FiPaperclip /> {ticketAttachment.name}
                                                </span>
                                                <span className="urbanq-support-file-size">
                                                    ({(ticketAttachment.size / 1024).toFixed(1)} KB)
                                                </span>
                                                <button
                                                    type="button"
                                                    className="urbanq-support-file-remove"
                                                    onClick={() => {
                                                        setTicketAttachment(null);
                                                        document.getElementById('ticket-attachment-input').value = '';
                                                    }}
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                        )}
                                        <p className="urbanq-support-file-hint">
                                            Formatos: JPG, PNG, GIF, PDF, DOC, DOCX. Máximo 5MB.
                                        </p>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-info">
                                    <FiInfo />
                                    <p>
                                        Al enviar este ticket, recibirás un correo de confirmación con tu número de ticket.
                                        Nuestro equipo responderá a <strong>{user.email}</strong>
                                    </p>
                                </div>

                                <div className="urbanq-support-form-actions">
                                    <button 
                                        type="button" 
                                        className="urbanq-support-btn secondary"
                                        onClick={() => setTicketForm({ subject: '', description: '', category: 'consulta', priority: 'media' })}
                                    >
                                        Limpiar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="urbanq-support-btn primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <FiRefreshCw className="spinning" /> Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <FiSend /> Enviar Ticket
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Info lateral */}
                        <div className="urbanq-support-sidebar-info">
                            <div className="urbanq-support-info-box">
                                <h4><FiClock /> Tiempos de Respuesta</h4>
                                <ul>
                                    <li><span className="priority-badge urgente">Urgente</span> 2-4 horas</li>
                                    <li><span className="priority-badge alta">Alta</span> 8-12 horas</li>
                                    <li><span className="priority-badge media">Media</span> 24 horas</li>
                                    <li><span className="priority-badge baja">Baja</span> 48 horas</li>
                                </ul>
                            </div>

                            <div className="urbanq-support-info-box">
                                <h4><FiMail /> Contacto Directo</h4>
                                <p>Para emergencias, contáctanos directamente:</p>
                                <a href="mailto:admin@nextunicodecorp.com" className="urbanq-support-contact-link">
                                    <FiMail /> admin@nextunicodecorp.com
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: BACKUP DE DATOS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'backup' && (
                    <div className="urbanq-support-backup-tab">
                        <div className="urbanq-support-section">
                            <h2><FiDownload /> Generar Backup de Datos</h2>
                            <p className="urbanq-support-section-desc">
                                Genera una copia de seguridad de toda la información de tu condominio.
                                El archivo se enviará a tu correo electrónico.
                            </p>

                            <div className="urbanq-support-backup-options">
                                <div className="urbanq-support-form-group">
                                    <label>Tipo de Backup</label>
                                    <div className="urbanq-support-radio-group">
                                        <label className={`urbanq-support-radio-option ${backupType === 'completo' ? 'selected' : ''}`}>
                                            <input 
                                                type="radio" 
                                                name="backupType" 
                                                value="completo"
                                                checked={backupType === 'completo'}
                                                onChange={(e) => setBackupType(e.target.value)}
                                            />
                                            <div className="urbanq-support-radio-content">
                                                <FiDatabase />
                                                <div>
                                                    <strong>Backup Completo</strong>
                                                    <span>Incluye usuarios, pagos, avisos y configuración</span>
                                                </div>
                                            </div>
                                        </label>
                                        
                                        <label className={`urbanq-support-radio-option ${backupType === 'usuarios' ? 'selected' : ''}`}>
                                            <input 
                                                type="radio" 
                                                name="backupType" 
                                                value="usuarios"
                                                checked={backupType === 'usuarios'}
                                                onChange={(e) => setBackupType(e.target.value)}
                                            />
                                            <div className="urbanq-support-radio-content">
                                                <FiUsers />
                                                <div>
                                                    <strong>Solo Usuarios</strong>
                                                    <span>Lista de residentes y administradores</span>
                                                </div>
                                            </div>
                                        </label>
                                        
                                        <label className={`urbanq-support-radio-option ${backupType === 'pagos' ? 'selected' : ''}`}>
                                            <input 
                                                type="radio" 
                                                name="backupType" 
                                                value="pagos"
                                                checked={backupType === 'pagos'}
                                                onChange={(e) => setBackupType(e.target.value)}
                                            />
                                            <div className="urbanq-support-radio-content">
                                                <FiDollarSign />
                                                <div>
                                                    <strong>Solo Pagos</strong>
                                                    <span>Historial de alícuotas y transacciones</span>
                                                </div>
                                            </div>
                                        </label>
                                        
                                        <label className={`urbanq-support-radio-option ${backupType === 'configuracion' ? 'selected' : ''}`}>
                                            <input 
                                                type="radio" 
                                                name="backupType" 
                                                value="configuracion"
                                                checked={backupType === 'configuracion'}
                                                onChange={(e) => setBackupType(e.target.value)}
                                            />
                                            <div className="urbanq-support-radio-content">
                                                <FiSettings />
                                                <div>
                                                    <strong>Solo Configuración</strong>
                                                    <span>Avisos, notificaciones y configuración</span>
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label>Formato de Exportación</label>
                                    <div className="urbanq-support-format-options">
                                        <label className={`urbanq-support-format-option ${backupFormat === 'xlsx' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="backupFormat"
                                                value="xlsx"
                                                checked={backupFormat === 'xlsx'}
                                                onChange={(e) => setBackupFormat(e.target.value)}
                                            />
                                            <div className="urbanq-support-format-content">
                                                <strong>📊 Excel (.xlsx)</strong>
                                                <span>Formato profesional con múltiples hojas</span>
                                            </div>
                                        </label>

                                        <label className={`urbanq-support-format-option ${backupFormat === 'csv' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="backupFormat"
                                                value="csv"
                                                checked={backupFormat === 'csv'}
                                                onChange={(e) => setBackupFormat(e.target.value)}
                                            />
                                            <div className="urbanq-support-format-content">
                                                <strong>📄 CSV</strong>
                                                <span>Compatible con cualquier hoja de cálculo</span>
                                            </div>
                                        </label>

                                        <label className={`urbanq-support-format-option ${backupFormat === 'json' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="backupFormat"
                                                value="json"
                                                checked={backupFormat === 'json'}
                                                onChange={(e) => setBackupFormat(e.target.value)}
                                            />
                                            <div className="urbanq-support-format-content">
                                                <strong>🔧 JSON</strong>
                                                <span>Formato técnico estructurado</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label className="urbanq-support-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={sendBackupEmail}
                                            onChange={(e) => setSendBackupEmail(e.target.checked)}
                                        />
                                        <span className="checkmark"></span>
                                        Enviar backup a mi correo ({user.email})
                                    </label>
                                </div>

                                <button 
                                    className="urbanq-support-btn primary large"
                                    onClick={handleGenerateBackup}
                                    disabled={generatingBackup}
                                >
                                    {generatingBackup ? (
                                        <>
                                            <FiRefreshCw className="spinning" /> Generando Backup...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload /> Generar Backup
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Historial de Backups */}
                        <div className="urbanq-support-section">
                            <h2><FiClock /> Historial de Backups</h2>
                            
                            {backups.length > 0 ? (
                                <div className="urbanq-support-backups-list">
                                    {backups.map(backup => (
                                        <div key={backup.id} className="urbanq-support-backup-item">
                                            <div className="urbanq-support-backup-icon">
                                                <FiDownload />
                                            </div>
                                            <div className="urbanq-support-backup-info">
                                                <span className="urbanq-support-backup-name">{backup.file_name}</span>
                                                <span className="urbanq-support-backup-meta">
                                                    {backup.backup_type} • {(backup.file_size / 1024).toFixed(2)} KB • 
                                                    {new Date(backup.created_at).toLocaleString('es-EC')}
                                                </span>
                                            </div>
                                            <div className="urbanq-support-backup-status">
                                                <span className={`urbanq-support-status-badge ${backup.status}`}>
                                                    {backup.status === 'completado' && <FiCheck />}
                                                    {backup.status}
                                                </span>
                                                {backup.email_sent_at && (
                                                    <span className="urbanq-support-email-sent">
                                                        <FiMail /> Enviado
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="urbanq-support-no-data">
                                    <FiDownload />
                                    <p>No hay backups generados aún</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: SEGURIDAD */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'security' && (
                    <div className="urbanq-support-security-tab">
                        <div className="urbanq-support-section">
                            <h2><FiLock /> Cambiar Contraseña</h2>
                            <p className="urbanq-support-section-desc">
                                Mantén tu cuenta segura actualizando tu contraseña periódicamente.
                            </p>

                            <form onSubmit={handleChangePassword} className="urbanq-support-password-form">
                                <div className="urbanq-support-form-group">
                                    <label>Contraseña Actual</label>
                                    <div className="urbanq-support-password-input">
                                        <input 
                                            type={showPasswords.current ? 'text' : 'password'}
                                            value={passwordForm.currentPassword}
                                            onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                                            placeholder="Ingresa tu contraseña actual"
                                            required
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                                        >
                                            {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label>Nueva Contraseña</label>
                                    <div className="urbanq-support-password-input">
                                        <input 
                                            type={showPasswords.new ? 'text' : 'password'}
                                            value={passwordForm.newPassword}
                                            onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                                            placeholder="Ingresa tu nueva contraseña"
                                            minLength={6}
                                            required
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                                        >
                                            {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>

                                <div className="urbanq-support-form-group">
                                    <label>Confirmar Nueva Contraseña</label>
                                    <div className="urbanq-support-password-input">
                                        <input 
                                            type={showPasswords.confirm ? 'text' : 'password'}
                                            value={passwordForm.confirmPassword}
                                            onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                                            placeholder="Confirma tu nueva contraseña"
                                            minLength={6}
                                            required
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                                        >
                                            {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>

                                <div className="urbanq-support-password-requirements">
                                    <h4>Requisitos de contraseña:</h4>
                                    <ul>
                                        <li className={passwordForm.newPassword.length >= 6 ? 'valid' : ''}>
                                            {passwordForm.newPassword.length >= 6 ? <FiCheck /> : <FiX />}
                                            Mínimo 6 caracteres
                                        </li>
                                        <li className={passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.newPassword ? 'valid' : ''}>
                                            {passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.newPassword ? <FiCheck /> : <FiX />}
                                            Las contraseñas coinciden
                                        </li>
                                    </ul>
                                </div>

                                <button 
                                    type="submit" 
                                    className="urbanq-support-btn primary"
                                    disabled={loading || passwordForm.newPassword.length < 6 || passwordForm.newPassword !== passwordForm.confirmPassword}
                                >
                                    {loading ? (
                                        <>
                                            <FiRefreshCw className="spinning" /> Actualizando...
                                        </>
                                    ) : (
                                        <>
                                            <FiShield /> Actualizar Contraseña
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Consejos de Seguridad */}
                        <div className="urbanq-support-section">
                            <h2><FiShield /> Consejos de Seguridad</h2>
                            <div className="urbanq-support-security-tips">
                                <div className="urbanq-support-tip">
                                    <FiCheck className="tip-icon" />
                                    <div>
                                        <strong>Usa contraseñas únicas</strong>
                                        <p>No uses la misma contraseña en múltiples sitios</p>
                                    </div>
                                </div>
                                <div className="urbanq-support-tip">
                                    <FiCheck className="tip-icon" />
                                    <div>
                                        <strong>Incluye caracteres especiales</strong>
                                        <p>Combina letras, números y símbolos (!@#$%)</p>
                                    </div>
                                </div>
                                <div className="urbanq-support-tip">
                                    <FiCheck className="tip-icon" />
                                    <div>
                                        <strong>Actualiza regularmente</strong>
                                        <p>Cambia tu contraseña cada 3-6 meses</p>
                                    </div>
                                </div>
                                <div className="urbanq-support-tip">
                                    <FiCheck className="tip-icon" />
                                    <div>
                                        <strong>No compartas credenciales</strong>
                                        <p>Nunca compartas tu contraseña con terceros</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

function ServiceStatusCard({ name, status, icon, detail }) {
    return (
        <div className={`urbanq-support-service-card ${status}`}>
            <div className="urbanq-support-service-icon">
                {icon}
            </div>
            <div className="urbanq-support-service-info">
                <span className="urbanq-support-service-name">{name}</span>
                <span className="urbanq-support-service-detail">{detail}</span>
            </div>
            <div className={`urbanq-support-service-status ${status}`}>
                {status === 'operativo' && <FiCheck />}
                {status === 'no disponible' && <FiAlertCircle />}
                {status === 'error' && <FiX />}
                <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
            </div>
        </div>
    );
}

function MetricCard({ label, value, icon, color }) {
    return (
        <div className={`urbanq-support-metric-card ${color}`}>
            <div className="urbanq-support-metric-icon">
                {icon}
            </div>
            <div className="urbanq-support-metric-content">
                <span className="urbanq-support-metric-value">{value}</span>
                <span className="urbanq-support-metric-label">{label}</span>
            </div>
        </div>
    );
}
